import { useEffect, type ReactNode } from "react";
import { Trans } from "@lingui/macro";
import { BookOpen } from "lucide-react";

import { Button } from "@app/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@app/components/ui/dialog";
import { cn } from "@app/lib/utils";

type EntryId =
  | "shapiro"
  | "welch"
  | "rank"
  | "chi2"
  | "fisher"
  | "ordinal"
  | "per_value"
  | "effects"
  | "bh"
  | "holm"
  | "km"
  | "logrank"
  | "logistic"
  | "checks"
  | "waterfall";

interface Entry {
  id: EntryId;
  name: ReactNode;
  when: ReactNode;
  reads: ReactNode;
}

interface Section {
  title: ReactNode;
  entries: Entry[];
}

/** The engine's test codes (`inference.TEST_*`), each to the entry that describes it. */
const ENTRY_OF_TEST: Record<string, EntryId> = {
  welch_t: "welch",
  welch_anova: "welch",
  mann_whitney: "rank",
  kruskal: "rank",
  chi2: "chi2",
  fisher: "fisher",
  fisher_mc: "fisher",
  per_value: "per_value",
  logrank: "logrank",
  logistic: "logistic",
};

/** A rank test run on an ordered scale is described by the scale's entry, not the numbers'. */
function entryOf(code: string, reason: string | null): EntryId | null {
  if (reason === "ordinal") return "ordinal";
  return ENTRY_OF_TEST[code] ?? null;
}

/**
 * The tests the engine can run, what each one answers and when it is chosen. A library to consult,
 * not a menu to pick from: the choice is made from the data, and a reader who wants another test
 * imposes it from the analysis's settings. Each entry says only what the engine does.
 */
const SECTIONS: Section[] = [
  {
    title: <Trans>Comparer des nombres (âge, poids, délai, score)</Trans>,
    entries: [
      {
        id: "shapiro",
        name: <Trans>Test de Shapiro-Wilk</Trans>,
        when: <Trans>Dans chaque groupe, avant de comparer.</Trans>,
        reads: (
          <Trans>
            Si la distribution est compatible avec une loi normale ; décide entre les deux familles de tests.
          </Trans>
        ),
      },
      {
        id: "welch",
        name: <Trans>Test t de Welch · ANOVA de Welch</Trans>,
        when: <Trans>Distributions normales ; deux groupes (t) ou davantage (ANOVA).</Trans>,
        reads: (
          <Trans>
            Deux groupes : une différence de moyennes avec son intervalle de confiance, sans supposer des
            variances égales. Davantage : un test global — au moins un groupe diffère — qui ne dit pas lequel.
          </Trans>
        ),
      },
      {
        id: "rank",
        name: <Trans>Test de Mann-Whitney · Kruskal-Wallis</Trans>,
        when: (
          <Trans>
            Distribution non normale dans un groupe (un délai en jours, souvent), ou groupe de moins de 3
            valeurs.
          </Trans>
        ),
        reads: (
          <Trans>
            Deux groupes : si les valeurs d'un groupe tendent à être plus grandes, avec le décalage de
            Hodges-Lehmann et les médianes. Davantage : un test global, sans décalage.
          </Trans>
        ),
      },
    ],
  },
  {
    title: <Trans>Comparer des catégories (oui/non, diagnostic, technique)</Trans>,
    entries: [
      {
        id: "chi2",
        name: <Trans>Test du χ²</Trans>,
        when: <Trans>Tous les effectifs attendus sont d'au moins 5 (correction de Yates sur un tableau 2×2).</Trans>,
        reads: (
          <Trans>Si les proportions diffèrent entre groupes ; odds ratio et différence de proportions sur un 2×2.</Trans>
        ),
      },
      {
        id: "fisher",
        name: <Trans>Test exact de Fisher</Trans>,
        when: <Trans>Un effectif attendu est inférieur à 5 ; Monte-Carlo à graine fixe au-delà du 2×2.</Trans>,
        reads: (
          <Trans>La même question que le χ², exacte sur de petits effectifs ; le même résultat à chaque lecture.</Trans>
        ),
      },
      {
        id: "ordinal",
        name: <Trans>Tests de rangs sur une échelle</Trans>,
        when: (
          <Trans>
            Modalités ordonnées déclarées dans l'ontologie par des intervalles chiffrés (durée de la douleur),
            et les années ; pas en mode paramétrique imposé. Un stade (ASA, Kellgren) ne se déclare pas encore
            ainsi : il est comparé comme une catégorie.
          </Trans>
        ),
        reads: <Trans>Utilise l'ordre que le χ² ignore : un groupe est-il plus haut sur l'échelle ?</Trans>,
      },
      {
        id: "per_value",
        name: <Trans>Un test par valeur, ajusté par Holm</Trans>,
        when: <Trans>Variable à plusieurs valeurs par patient (antécédents, complications).</Trans>,
        reads: (
          <Trans>
            Quelle valeur distingue les groupes ; un χ² unique compterait deux fois un patient à deux valeurs.
          </Trans>
        ),
      },
    ],
  },
  {
    title: <Trans>Mesurer l'ampleur d'une différence</Trans>,
    entries: [
      {
        id: "effects",
        name: <Trans>Tailles d'effet</Trans>,
        when: <Trans>À chaque test, dans la ligne dépliée.</Trans>,
        reads: (
          <Trans>
            d de Cohen (Welch), corrélation rang-bisériale (Mann-Whitney), η² et ε² (plus de deux groupes),
            V de Cramér (catégories) : une p-valeur dit si la différence est probable, la taille d'effet dit si
            elle compte.
          </Trans>
        ),
      },
    ],
  },
  {
    title: <Trans>Tenir compte de plusieurs tests (tableau seulement)</Trans>,
    entries: [
      {
        id: "bh",
        name: <Trans>Benjamini-Hochberg</Trans>,
        when: <Trans>Par défaut : un tableau teste toutes les variables qu'il liste.</Trans>,
        reads: <Trans>Limite la proportion de fausses découvertes parmi les résultats significatifs.</Trans>,
      },
      {
        id: "holm",
        name: <Trans>Holm · Bonferroni</Trans>,
        when: <Trans>Quelques hypothèses fixées à l'avance ; à choisir dans les réglages du tableau.</Trans>,
        reads: (
          <Trans>
            Limite le risque d'un seul faux positif ; Holm est toujours au moins aussi puissant que Bonferroni.
          </Trans>
        ),
      },
    ],
  },
  {
    title: <Trans>Suivre un score patient par patient</Trans>,
    entries: [
      {
        id: "waterfall",
        name: <Trans>Graphe en cascade</Trans>,
        when: (
          <Trans>
            Un score suivi dans le temps (Oswestry, EVA, KOOS…) : la dernière mesure avant l'acte contre la
            mesure la plus proche de l'horizon choisi.
          </Trans>
        ),
        reads: (
          <Trans>
            Une barre par patient, triée : qui s'améliore, de combien, et quelle part franchit le seuil que
            vous fixez. Sur une cohorte vue seulement en agrégat, un résumé remplace les barres.
          </Trans>
        ),
      },
    ],
  },
  {
    title: <Trans>Analyser un délai jusqu'à un événement</Trans>,
    entries: [
      {
        id: "km",
        name: <Trans>Kaplan-Meier</Trans>,
        when: <Trans>Survie sans reprise ; délai jusqu'à l'intervention.</Trans>,
        reads: (
          <Trans>
            La proportion encore sans événement au fil du temps, en comptant les patients suivis moins longtemps
            (censure).
          </Trans>
        ),
      },
      {
        id: "logrank",
        name: <Trans>Test du log-rank · hazard ratio de Peto</Trans>,
        when: <Trans>Au moins deux groupes ; le hazard ratio pour deux groupes seulement.</Trans>,
        reads: (
          <Trans>Si les courbes diffèrent sur tout le suivi ; le rapport des risques instantanés du second groupe.</Trans>
        ),
      },
    ],
  },
  {
    title: <Trans>Mesurer l'effet de plusieurs facteurs à la fois</Trans>,
    entries: [
      {
        id: "logistic",
        name: <Trans>Régression logistique</Trans>,
        when: <Trans>Un événement oui/non et des facteurs à ajuster entre eux.</Trans>,
        reads: (
          <Trans>
            Odds ratio de chaque facteur, brut puis ajusté sur les autres, avec intervalle de confiance ; une
            modalité est toujours comparée à la modalité de référence.
          </Trans>
        ),
      },
      {
        id: "checks",
        name: <Trans>Événements par paramètre · séparation</Trans>,
        when: <Trans>Contrôlés à chaque modèle, et signalés sans bloquer.</Trans>,
        reads: (
          <Trans>
            Moins de 10 événements par paramètre estimé (chaque modalité en compte un) rend le modèle instable ;
            un facteur qui prédit parfaitement l'événement n'a pas d'estimation finie.
          </Trans>
        ),
      },
    ],
  },
];

function anchor(id: EntryId): string {
  return `test-library-${id}`;
}

function LibraryBody({ focus }: { focus: EntryId | null }) {
  useEffect(() => {
    if (!focus) return;
    // After the dialog has laid itself out, or the entry is scrolled to before it has a position.
    const frame = requestAnimationFrame(() =>
      document.getElementById(anchor(focus))?.scrollIntoView({ block: "center" }),
    );
    return () => cancelAnimationFrame(frame);
  }, [focus]);

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          <Trans>Bibliothèque des tests</Trans>
        </DialogTitle>
      </DialogHeader>
      <p className="text-sm text-slate-600">
        <Trans>
          Le test n'est pas choisi dans une liste : le moteur le déduit du type de chaque variable et de vos
          données. La raison s'affiche au survol du nom du test et dans la ligne dépliée. Les réglages du tableau
          permettent d'imposer un test paramétrique ou non paramétrique, et de changer la correction multi-tests.
        </Trans>
      </p>
      <div className="space-y-5">
        {SECTIONS.map((section, index) => (
          <section key={index} className="space-y-2">
            <h3 className="text-sm font-semibold text-slate-900">{section.title}</h3>
            <div className="divide-y divide-slate-100 rounded-lg border border-slate-200">
              {section.entries.map((entry) => (
                <div
                  key={entry.id}
                  id={anchor(entry.id)}
                  className={cn(
                    "grid gap-1 px-3 py-2.5 text-xs sm:grid-cols-[12rem_1fr]",
                    entry.id === focus && "bg-primary/5 ring-1 ring-inset ring-primary/40",
                  )}
                >
                  <div className="font-medium text-primary">{entry.name}</div>
                  <div className="space-y-0.5 text-slate-600">
                    <div>
                      <span className="text-slate-400">
                        <Trans>Quand :</Trans>
                      </span>{" "}
                      {entry.when}
                    </div>
                    <div>
                      <span className="text-slate-400">
                        <Trans>Ce qu'il dit :</Trans>
                      </span>{" "}
                      {entry.reads}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
    </>
  );
}

export function TestLibrary() {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-1.5 hover:border-primary/50 hover:text-primary">
          <BookOpen className="h-4 w-4" />
          <Trans>Bibliothèque des tests</Trans>
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
        <LibraryBody focus={null} />
      </DialogContent>
    </Dialog>
  );
}

/**
 * A result's test name, opening the library on its entry; the reason it was chosen shows on hover.
 * A test the library does not describe — « non testé » — stays plain text.
 */
export function TestName({
  code,
  name,
  reason,
  reasonText,
}: {
  code: string;
  name: string;
  reason: string | null;
  reasonText: string | null;
}) {
  const entry = entryOf(code, reason);
  if (!entry) return <span title={reasonText ?? undefined}>{name}</span>;
  return (
    // Events from the dialog's portal bubble through the React tree: stopped here, a click inside the
    // library does not also fold the table row the name sits in.
    <span onClick={(event) => event.stopPropagation()}>
      <Dialog>
        <DialogTrigger asChild>
          <button
            type="button"
            title={reasonText ?? undefined}
            className="text-left underline decoration-slate-300 decoration-dotted underline-offset-2 hover:text-primary hover:decoration-primary/50"
          >
            {name}
          </button>
        </DialogTrigger>
        <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
          <LibraryBody focus={entry} />
        </DialogContent>
      </Dialog>
    </span>
  );
}
